import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format, getUnixTime, parseISO } from 'date-fns';
import type AjaxService from 'garaje/services/ajax';
import type CurrentAdminService from 'garaje/services/current-admin';
import urlBuilder from 'garaje/utils/url-builder';

interface MoveRequestProfileResponse {
  employeeName: string;
  department: string | null;
  assignedDesks: AssignedDeskInfo[];
  assignedParking: AssignedParkingInfo | null;
  moveRequestStatuses: MoveRequestStatus[];
  canRequest: boolean;
}

interface AssignedDeskInfo {
  deskId: number;
  clientId: string;
  name: string;
  floorId: number;
  floorName: string | null;
  locationId: number;
  locationName: string | null;
  neighborhoodId: number | null;
  neighborhoodName: string | null;
}

interface AssignedParkingInfo {
  resourceId: number;
  name: string;
  locationId: number;
  locationName: string | null;
}

interface MoveRequestStatus {
  moveRequestId: string;
  statusType: 'PENDING_REQUEST' | 'UPCOMING_CHANGE';
  changeType: 'ASSIGNMENT' | 'CHANGE' | 'UNASSIGNMENT';
  assignmentId: number;
  newDeskName: string | null;
  newFloorName: string | null;
  locationId: number;
  locationName: string | null;
  date: string | null;
}

interface DisplayStatus {
  moveRequestId: string;
  assignmentId: number;
  title: string;
  newDeskLine: string | null;
  dateLine: string | null;
  showCancelLink: boolean;
}

interface DisplayDesk {
  deskId: number;
  displayText: string;
  neighborhoodName: string | null;
}

interface ProfileCardArgs {
  onClose: () => void;
  onRequestChange?: () => void;
  onCancelRequest?: (moveRequestId: string) => void;
  onProfileLoaded?: (data: MoveRequestProfileResponse) => void;
  locationId?: number | string;
  selectedDate?: Date | null;
}

export default class MapsProfileCardComponent extends Component<{ Args: ProfileCardArgs }> {
  @service declare ajax: AjaxService;
  @service declare currentAdmin: CurrentAdminService;

  @tracked profileData: MoveRequestProfileResponse | null = null;
  @tracked isLoading = true;
  @tracked hasError = false;

  get userPhotoUrl(): string | undefined {
    return this.currentAdmin.user?.userPhotoUrl;
  }

  get currentLocationDesks(): AssignedDeskInfo[] {
    const locationId = this.args.locationId != null ? Number(this.args.locationId) : null;
    if (!locationId) return this.profileData?.assignedDesks ?? [];
    return (this.profileData?.assignedDesks ?? []).filter((d) => d.locationId === locationId);
  }

  get otherLocationDesks(): AssignedDeskInfo[] {
    const locationId = this.args.locationId != null ? Number(this.args.locationId) : null;
    if (!locationId) return [];
    return (this.profileData?.assignedDesks ?? []).filter((d) => d.locationId !== locationId);
  }

  get hasDesks(): boolean {
    return this.currentLocationDesks.length > 0;
  }

  get desks(): DisplayDesk[] {
    return this.currentLocationDesks.map((desk) => ({
      deskId: desk.deskId,
      displayText: [desk.name, desk.floorName].filter(Boolean).join(', '),
      neighborhoodName: desk.neighborhoodName,
    }));
  }

  get otherDesks(): DisplayDesk[] {
    return this.otherLocationDesks.map((desk) => ({
      deskId: desk.deskId,
      displayText: [desk.name, desk.floorName, desk.locationName].filter(Boolean).join(', '),
      neighborhoodName: desk.neighborhoodName,
    }));
  }

  get parkingText(): string | null {
    const parking = this.profileData?.assignedParking;
    if (!parking) return null;
    return [parking.name, parking.locationName].filter(Boolean).join(', ');
  }

  get upcomingStatuses(): DisplayStatus[] {
    return (this.profileData?.moveRequestStatuses ?? [])
      .filter((s) => s.statusType === 'UPCOMING_CHANGE')
      .map((s) => ({
        moveRequestId: s.moveRequestId,
        assignmentId: s.assignmentId,
        title: `You have an upcoming desk ${s.changeType.toLowerCase()}`,
        newDeskLine: s.newDeskName ? `New desk: ${s.newDeskName}${s.newFloorName ? `, ${s.newFloorName}` : ''}` : null,
        dateLine: s.date ? `Date: ${format(parseISO(s.date), 'MMM dd, yyyy')}` : null,
        showCancelLink: false,
      }));
  }

  get pendingStatuses(): DisplayStatus[] {
    return (this.profileData?.moveRequestStatuses ?? [])
      .filter((s) => s.statusType === 'PENDING_REQUEST')
      .map((s) => ({
        moveRequestId: s.moveRequestId,
        assignmentId: s.assignmentId,
        title: `Your desk ${s.changeType.toLowerCase()} request is pending`,
        newDeskLine: s.newDeskName ? `New desk: ${s.newDeskName}${s.newFloorName ? `, ${s.newFloorName}` : ''}` : null,
        dateLine: null,
        showCancelLink: true,
      }));
  }

  get buttonText(): string {
    return this.hasDesks ? 'Request desk change' : 'Request desk assignment';
  }

  get canRequest(): boolean {
    return this.profileData?.canRequest ?? false;
  }

  @action
  async loadProfile(): Promise<void> {
    try {
      this.isLoading = true;
      this.hasError = false;

      const employeeId = this.currentAdmin.employee?.id;
      if (!employeeId) {
        this.hasError = true;
        return;
      }

      const selectedDate = this.args.selectedDate;
      // Workplace's /api/move-requests/profile expects `date` as a unix
      // timestamp (seconds since epoch) — the instant of start-of-day in the
      // viewed location's timezone. `selectedDate` is already computed in
      // location-TZ terms by spaces/maps/live/show/controller.ts, so
      // getUnixTime gives the exact value the downstream RMS call needs.
      const selectedUnix = selectedDate ? getUnixTime(selectedDate) : undefined;

      this.profileData = await this.ajax.request<MoveRequestProfileResponse>(
        urlBuilder.moveRequests.profileUrl(employeeId, selectedUnix),
      );
      if (this.profileData) {
        this.args.onProfileLoaded?.(this.profileData);
      }
    } catch {
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }
}
